import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Step, StepLabel, Stepper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { backendDateFormat, dateMonthYearDateFormat, fieldDateFormat } from '../../../constants'
import { getBanksAc, getOwnerListApi, getStorage } from '../../../shared_elements/actionCreators'
import fileUpload, { formatBytes } from '../../../utils/fileUpload'
import { setFileUploadDetails, setUploadFiles, setBanks } from '../../../shared_elements/actions'
import { AttachementsUpload } from '../../../shared_elements/components'
import { globalGetService, globalPostService } from '../../../utils/globalApiServices'
import { checkApiStatus } from '../../../utils'
import { addInvoiceApi, addManagementFeeApi, getAdditionalTaxDropdownAPI, getAssetListApi, getManagementFeeInitApi } from '../apiService'
import { regexConstants } from '../../../constants/regEx'
import { trackActivity } from '../../../utils/mixpanel'
import NumericLabel from 'react-pretty-numbers';

const fmtCurrency = (amount) => {
    if (!amount && amount !== 0) return '--'
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

const incrementInvoiceNumber = (baseNumber, incrementBy) => {
    if (!baseNumber || !incrementBy) return baseNumber
    const match = baseNumber.match(/(\d+)(?!.*\d)/)
    if (!match) return baseNumber
    const numStr = match[1]
    const nextNum = String(Number(numStr) + incrementBy).padStart(numStr.length, '0')
    return baseNumber.slice(0, match.index) + nextNum + baseNumber.slice(match.index + numStr.length)
}

// Required + uniqueness validation for the per-Lessor-group Invoice Number fields (non-createFromInv flow only)
const validateGroupInvoiceNumbers = (numbers, groups) => {
    const errors = {}
    const seenAt = new Map()
    groups.forEach(group => {
        const value = (numbers[group.lessorId] || '').trim()
        if (!value) {
            errors[group.lessorId] = 'Please enter Invoice Number'
            return
        }
        if (!seenAt.has(value)) seenAt.set(value, [])
        seenAt.get(value).push(group.lessorId)
    })
    seenAt.forEach(lessorIds => {
        if (lessorIds.length > 1) {
            lessorIds.forEach(lessorId => { errors[lessorId] = 'Invoice number must be unique.' })
        }
    })
    return errors
}

const EditableLineItemRow = ({ invoice, mgmtFeePct, feeAmount, taxRate, taxAmount, subTotal, showTaxColumns, showRentalColumns, errors, onMgmtFeePctChange, onFeeAmountChange, onClearError }) => {
    const baseAmount = invoice.rate_per_unit || 0
    const mgmtPctFocused = useRef(false)
    const feeAmtFocused = useRef(false)
    const [localMgmtPct, setLocalMgmtPct] = useState(() => parseFloat((mgmtFeePct || 0).toFixed(2)).toString())
    const [localFeeAmt, setLocalFeeAmt] = useState(() => (feeAmount || 0).toFixed(2))

    useEffect(() => {
        if (!mgmtPctFocused.current) setLocalMgmtPct(parseFloat((mgmtFeePct || 0).toFixed(2)).toString())
    }, [mgmtFeePct])

    useEffect(() => {
        if (!feeAmtFocused.current) setLocalFeeAmt((feeAmount || 0).toFixed(2))
    }, [feeAmount])

    const handleMgmtPctChange = (e) => {
        const clean = e.target.value.replace(/[^0-9.]/g, '')
        if (clean === '' || (regexConstants.percantageRegExp.test(clean) && !/^100\./.test(clean))) {
            const value = clean.replace(/^0+(\d)/, '$1')
            setLocalMgmtPct(value)
            onMgmtFeePctChange(value)
            if (parseFloat(value) > 0) onClearError('management_fee_tax')
        }
    }

    const handleFeeAmtChange = (e) => {
        const clean = e.target.value.replace(/[^0-9.]/g, '')
        if (clean === '' || (regexConstants.decimalnodotstart.test(clean) && parseFloat(clean) <= baseAmount)) {
            setLocalFeeAmt(clean)
            onFeeAmountChange(clean)
            if (parseFloat(clean) > 0) onClearError('feeAmount')
        }
    }

    return (
        <TableRow>
            <TableCell>
                <Typography variant="body2" fontWeight={500}>{invoice.asset_details?.name || '--'}</Typography>
                {!showRentalColumns && <Typography variant="caption" color="text.secondary">{invoice.invoice_item_name || ''}</Typography>}
            </TableCell>
            {showRentalColumns && <TableCell sx={{ fontSize: 12 }}>{invoice.invoice_item_name || '--'}</TableCell>}
            {showRentalColumns && (
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {invoice.invoice_period_start && invoice.invoice_period_end
                        ? `${moment(invoice.invoice_period_start).format(dateMonthYearDateFormat)} - ${moment(invoice.invoice_period_end).format(dateMonthYearDateFormat)}`
                        : '--'}
                </TableCell>
            )}
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtCurrency(baseAmount)}</TableCell>
            <TableCell align="center">
                <TextField
                    size="small"
                    value={localMgmtPct}
                    onChange={handleMgmtPctChange}
                    onFocus={() => { mgmtPctFocused.current = true; onClearError('management_fee_tax') }}
                    onBlur={() => { mgmtPctFocused.current = false; setLocalMgmtPct(parseFloat((mgmtFeePct || 0).toFixed(2)).toString()) }}
                    error={!!errors?.management_fee_tax}
                    helperText={errors?.management_fee_tax || ''}
                    slotProps={{ input: { inputProps: { style: { textAlign: 'center', padding: '4px 6px', fontSize: 12, width: 70 } } } }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }}
                />
            </TableCell>
            <TableCell align="center">
                <TextField
                    size="small"
                    value={localFeeAmt}
                    onChange={handleFeeAmtChange}
                    onFocus={() => { feeAmtFocused.current = true; onClearError('feeAmount') }}
                    onBlur={() => { feeAmtFocused.current = false; setLocalFeeAmt((feeAmount || 0).toFixed(2)) }}
                    error={!!errors?.feeAmount}
                    helperText={errors?.feeAmount || ''}
                    slotProps={{ input: { inputProps: { style: { textAlign: 'right', padding: '4px 6px', fontSize: 12, width: 90 } } } }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }}
                />
            </TableCell>
            {showTaxColumns && <TableCell align="center" sx={{ fontSize: 12 }}>{taxRate}%</TableCell>}
            {showTaxColumns && <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtCurrency(taxAmount)}</TableCell>}
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtCurrency(subTotal)}</TableCell>
        </TableRow>
    )
}

const GenerateManagementFeeModal = ({ onToggleModal , invoiceDetails, createFromInv, from, getResponseBack }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { currencies, banks, owner, storageDetails } = useSelector(state => state.leases)

    const [activeStep, setActiveStep] = useState(createFromInv ? 1 : 0)
    const [filters, setFilters] = useState({ lessor: [], start_date: null, end_date: null, currency: null, asset: [], additional_tax_type_id: null })
    const [eligibleInvoices, setEligibleInvoices] = useState([])
    const [tableLoader, setTableLoader] = useState(false)
    const [selectedInvIds, setSelectedInvIds] = useState([])
    const [invoiceHeader, setInvoiceHeader] = useState({
        invoice_number: '', date: null, due_date: null, bank: null, from: null, notes: ''
    })
    const [assetList, setAssetList] = useState([])
    const [assetLoader, setAssetLoader] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [generatedInvoicesModal, setGeneratedInvoicesModal] = useState({ modal: false, data: [] })
    const [headerError, setHeaderError] = useState({})
    const [attachments, setAttachments] = useState([])
    const [filterError, setFilterError] = useState({})
    const [additionalTax, setAdditionalTax] = useState([])
    const [additionalTaxLoader, setAdditionalTaxLoader] = useState(false)
    const [lineItemOverrides, setLineItemOverrides] = useState({})
    const [lineItemErrors, setLineItemErrors] = useState({})
    const [banksLoader, setBanksLoader] = useState(false)
    const [noBanksMsg, setNoBanksMsg] = useState('')
    const [groupInvoiceNumbers, setGroupInvoiceNumbers] = useState({})
    const [editedGroupInvoiceNumbers, setEditedGroupInvoiceNumbers] = useState({})
    const [groupInvoiceNumberErrors, setGroupInvoiceNumberErrors] = useState({})
    const [backendGroupInvoiceNumberErrors, setBackendGroupInvoiceNumberErrors] = useState({})

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Generate Management Fee', page_type: page_type })
    }

    useEffect(() => {
        if (!owner?.length) dispatch(getOwnerListApi({}))
        dispatch(getStorage({ module_name: 'invoices' }))
        dispatch(setBanks([]))
        getAssetList(invoiceDetails?.lessor?.id ? [invoiceDetails.lessor.id] : [])
        getAdditionalTaxDropdown()
    }, [])

    useEffect(() => {
        if (createFromInv) 
            setActiveStep(1)
            fetchEligibleInvoicesFromInv()
    }, [createFromInv])

    const getAssetList = (lessorIds) => {
        if (!lessorIds?.length) {
            setAssetList([])
            return
        }
        setAssetLoader(true)
        globalGetService(getAssetListApi({ is_invoice: true, ownership_type: 2, lessor_name_id: lessorIds.join(',') })).then(response => {
            if (checkApiStatus(response)) setAssetList(response?.data?.data || [])
            setAssetLoader(false)
        })
    }

    const getAdditionalTaxDropdown = () => {
        setAdditionalTaxLoader(true)
        globalGetService(getAdditionalTaxDropdownAPI()).then(response => {
            if (checkApiStatus(response)) setAdditionalTax(response?.data?.data?.list || [])
            setAdditionalTaxLoader(false)
        })
    }

    const fetchBanksForServicer = (servicerId) => {
        setNoBanksMsg('')
        if (!servicerId) {
            dispatch(setBanks([]))
            return
        }
        setBanksLoader(true)
        dispatch(getBanksAc({ lessor_id: servicerId })).then(bankRecords => {
            setNoBanksMsg(bankRecords?.length === 0 ? 'No bank linked to this servicer.' : '')
        }).finally(() => setBanksLoader(false))
    }

    const onServicerChange = (val) => {
        setInvoiceHeader(h => ({ ...h, from: val, bank: null }))
        onResetErrorKey('from')
        fetchBanksForServicer(val?.id)
    }
    const isMandatoryFilled = (f) =>
        f.lessor?.length > 0 && f.start_date && f.end_date && f.currency?.id

    const fetchEligibleInvoices = (currentFilters) => {
        if (!isMandatoryFilled(currentFilters)) return

        const query = {
            lessor_ids: `[${currentFilters.lessor.map(l => l.id).join(',')}]`,
            currency: currentFilters.currency.id,
            period_start_date: currentFilters.start_date,
            period_end_date: currentFilters.end_date,
            invoice_type: 2,
        }
        if (currentFilters.asset?.length > 0) {
            query.assets = JSON.stringify(currentFilters.asset.map(a => ({ type: a.asset_type, id: a.id })))
        }
        if (currentFilters.additional_tax_type_id) {
           query.additional_tax_type_id = currentFilters.additional_tax_type_id.id
        }

        setTableLoader(true)
        setEligibleInvoices([])
        setSelectedInvIds([])
        setGroupInvoiceNumbers({})
        setEditedGroupInvoiceNumbers({})
        setGroupInvoiceNumberErrors({})
        setBackendGroupInvoiceNumberErrors({})
        globalGetService(getManagementFeeInitApi(query)).then(response => {
            setTableLoader(false)
            if (checkApiStatus(response)) {
                const data = response?.data?.data
                const items = data?.invoice_items || []
                setEligibleInvoices(items)
                setSelectedInvIds(items.map(inv => inv.sub_reference_id))
                if (data?.invoice_number) {
                    setInvoiceHeader(h => ({ ...h, invoice_number: data.invoice_number }))
                }
            }
        })
    }

    const fetchEligibleInvoicesFromInv = () => {
        if (!invoiceDetails?.lessor?.id || !invoiceDetails?.currency?.id) return

        setFilters(prev => ({
            ...prev,
            lessor: [invoiceDetails.lessor],
            currency: invoiceDetails.currency,
            start_date: invoiceDetails.start_date || null,
            end_date: invoiceDetails.end_date || null,
        }))

        const query = {
            lessor_ids: `[${invoiceDetails.lessor.id}]`,
            currency: invoiceDetails.currency.id,
            period_start_date: invoiceDetails.start_date || '',
            period_end_date: invoiceDetails.end_date || '',
            invoice_type: 2,
        }
        if (invoiceDetails.slug) query.invoice_slug = invoiceDetails.slug

        setTableLoader(true)
        setEligibleInvoices([])
        setSelectedInvIds([])
        setGroupInvoiceNumbers({})
        setEditedGroupInvoiceNumbers({})
        setGroupInvoiceNumberErrors({})
        setBackendGroupInvoiceNumberErrors({})
        globalGetService(getManagementFeeInitApi(query)).then(response => {
            setTableLoader(false)
            if (checkApiStatus(response)) {
                const data = response?.data?.data
                const items = data?.invoice_items || []
                setEligibleInvoices(items)
                setSelectedInvIds(items.map(inv => inv.sub_reference_id))
                if (data?.invoice_number) {
                    setInvoiceHeader(h => ({ ...h, invoice_number: data.invoice_number }))
                }
            } else {
                enqueueSnackbar(response?.data?.message || 'Failed to load invoice data', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
            }
        })
    }

    const handleFilterChange = (field, value) => {
        const newFilters = { ...filters, [field]: value }
        setFilters(newFilters)
        if (isMandatoryFilled(newFilters)) {
            fetchEligibleInvoices(newFilters)
        } else {
            // If mandatory fields become incomplete, clear table
            setEligibleInvoices([])
            setSelectedInvIds([])
        }
    }

    const onLessorChange = (val) => {
        const selectedLessors = val || []
        getAssetList(selectedLessors.map(l => l.id))
        const newFilters = { ...filters, lessor: selectedLessors, asset: [] }
        setFilters(newFilters)
        if (isMandatoryFilled(newFilters)) {
            fetchEligibleInvoices(newFilters)
        } else {
            setEligibleInvoices([])
            setSelectedInvIds([])
        }
    }

    const getEffectiveValues = (inv) => {
        const override = lineItemOverrides[inv.sub_reference_id]
        const baseAmount = inv.rate_per_unit || 0
        const hasTax = (inv.tax_details?.length || 0) > 0
        const taxRate = hasTax ? (inv.tax_details[0]?.tax_percentage || 0) : 0
        const mgmtFeePct = override !== undefined ? override.management_fee_tax : (inv.management_fee_tax || 0)
        const feeAmount = override !== undefined ? override.feeAmount : baseAmount * mgmtFeePct / 100
        const taxAmount = hasTax && taxRate > 0 ? parseFloat((feeAmount * taxRate / 100).toFixed(2)) : 0
        const subTotal = parseFloat((feeAmount + taxAmount).toFixed(2))
        return { baseAmount, mgmtFeePct, feeAmount, taxRate, taxAmount, subTotal, hasTax }
    }

    const clearBothIfValid = (sub_reference_id, pct, fee) => {
        setLineItemErrors(prev => {
            if (!prev[sub_reference_id]) return prev
            const updated = { ...prev[sub_reference_id] }
            if (pct > 0) updated.management_fee_tax = ''
            if (fee > 0) updated.feeAmount = ''
            return { ...prev, [sub_reference_id]: updated }
        })
    }

    const handleMgmtFeePctChange = (inv, rawValue) => {
        const baseAmount = inv.rate_per_unit || 0
        const parsed = parseFloat(rawValue)
        const pct = isNaN(parsed) || parsed < 0 ? 0 : parsed
        const feeAmount = baseAmount * pct / 100
        setLineItemOverrides(prev => ({ ...prev, [inv.sub_reference_id]: { management_fee_tax: pct, feeAmount } }))
        clearBothIfValid(inv.sub_reference_id, pct, feeAmount)
    }

    const handleFeeAmountChange = (inv, rawValue) => {
        const baseAmount = inv.rate_per_unit || 0
        const parsed = parseFloat(rawValue)
        const fee = isNaN(parsed) || parsed < 0 ? 0 : parsed
        const pct = baseAmount > 0 ? (fee / baseAmount) * 100 : 0
        setLineItemOverrides(prev => ({ ...prev, [inv.sub_reference_id]: { management_fee_tax: pct, feeAmount: fee } }))
        clearBothIfValid(inv.sub_reference_id, pct, fee)
    }

    const clearLineItemError = (sub_reference_id, field) => {
        setLineItemErrors(prev => {
            if (!prev[sub_reference_id]) return prev
            return { ...prev, [sub_reference_id]: { ...prev[sub_reference_id], [field]: '' } }
        })
    }

    // Eligible invoice table helpers
    const allSelected = eligibleInvoices.length > 0 && selectedInvIds.length === eligibleInvoices.length
    const someSelected = selectedInvIds.length > 0 && selectedInvIds.length < eligibleInvoices.length


    const toggleSelectAll = (e) => {
        setSelectedInvIds(e.target.checked ? eligibleInvoices.map(inv => inv.sub_reference_id) : [])
    }
    const toggleRow = (id) => {
        setSelectedInvIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
    }

    const selectedInvoices = eligibleInvoices.filter(inv => selectedInvIds.includes(inv.sub_reference_id))

    const lessorGroups = useMemo(() => {
        const map = new Map()
        selectedInvoices.forEach(inv => {
            const key = inv.lessor_holdco_id ?? 'unassigned'
            if (!map.has(key)) {
                map.set(key, { lessorId: key, lessorName: inv.lessor_holdco_name || '--', items: [] })
            }
            map.get(key).items.push(inv)
        })
        return Array.from(map.values())
    }, [selectedInvoices])

    const lessorGroupIdsKey = lessorGroups.map(group => group.lessorId).join(',')

    // Regenerates every non-user-edited group's Invoice Number from the immutable API base number whenever
    // the set/order of Lessor groups changes (e.g. Step 1 selection changes), rather than only filling gaps -
    // otherwise a group's number stays pinned to its old index after groups are added/removed.
    useEffect(() => {
        if (!invoiceHeader.from?.id || !invoiceHeader.invoice_number || lessorGroups.length === 0) return
        setGroupInvoiceNumbers(prev => {
            let changed = false
            const next = { ...prev }
            lessorGroups.forEach((group, index) => {
                if (editedGroupInvoiceNumbers[group.lessorId]) return
                const generated = index === 0 ? invoiceHeader.invoice_number : incrementInvoiceNumber(invoiceHeader.invoice_number, index)
                if (next[group.lessorId] !== generated) {
                    next[group.lessorId] = generated
                    changed = true
                }
            })
            return changed ? next : prev
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceHeader.from?.id, invoiceHeader.invoice_number, lessorGroupIdsKey, editedGroupInvoiceNumbers])

    const onGroupInvoiceNumberChange = (lessorId, value) => {
        setGroupInvoiceNumbers(prev => ({ ...prev, [lessorId]: value }))
        setEditedGroupInvoiceNumbers(prev => (prev[lessorId] ? prev : { ...prev, [lessorId]: true }))
        setBackendGroupInvoiceNumberErrors(prev => {
            if (!prev[lessorId]) return prev
            const next = { ...prev }
            delete next[lessorId]
            return next
        })
    }

    useEffect(() => {
        if (createFromInv) {
            setGroupInvoiceNumberErrors({})
            return
        }
        setGroupInvoiceNumberErrors(validateGroupInvoiceNumbers(groupInvoiceNumbers, lessorGroups))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createFromInv, groupInvoiceNumbers, lessorGroupIdsKey])

    const calcFee = (inv) => (inv.rate_per_unit || 0) * (inv.management_fee_tax || 0) / 100

    const totalFee = selectedInvoices.reduce((sum, inv) => sum + (inv.rate_per_unit || 0), 0)
    const totalCalculatedFee = selectedInvoices.reduce((sum, inv) => sum + calcFee(inv), 0)

    const handleProceedToStep2 = () => {
        setActiveStep(1)
    }

    const grandTax = selectedInvoices.reduce((sum, inv) => sum + getEffectiveValues(inv).feeAmount, 0)
    const grandVat = selectedInvoices.reduce((sum, inv) => sum + getEffectiveValues(inv).taxAmount, 0)
    const grandTotal = selectedInvoices.reduce((sum, inv) => sum + getEffectiveValues(inv).subTotal, 0)
    const hasTaxDetails = selectedInvoices.some(inv => inv.tax_details?.length > 0)
    const invoiceCurrency = invoiceDetails?.currency?.code || invoiceDetails?.currency?.name || (typeof invoiceDetails?.currency === 'string' ? invoiceDetails.currency : '')

    const onResetErrorKey = (keyParam) => {
        setHeaderError(prev => ({ ...prev, [keyParam]: '' }))
    }

    const handleFileChange = (files) => {
        setAttachments(files)
        dispatch(setFileUploadDetails({ attachments: files }))
    }

    const buildInvoiceItemsPayload = (items) => items.map(inv => {
        const { mgmtFeePct, feeAmount, taxRate, taxAmount, subTotal } = getEffectiveValues(inv)
        return {
            invoice_item_name: inv.invoice_item_name || null,
            description: inv.description || null,
            unit_description: null,
            no_of_units: inv.no_of_units || 0,
            rate_per_unit: inv.rate_per_unit || 0,
            sub_total: inv.rate_per_unit || 0,
            management_fee_tax: mgmtFeePct,
            tax_rate: taxRate,
            tax_amount: parseFloat(taxAmount.toFixed(2)),
            sub_amount_total: parseFloat(subTotal.toFixed(2)),
            asset: { asset_type: inv.asset_type || null, asset_id: inv.asset_id || null },
            tax_details: inv.tax_details?.length
                ? [{ ...inv.tax_details[0], tax_amount: parseFloat(taxAmount.toFixed(2)) }]
                : null,
            sub_reference_id: inv.sub_reference_id || null
        }
    })

    const onGenerateInvoice = () => {
        const errors = {}
        if (!invoiceHeader.invoice_number?.trim()) errors.invoice_number = 'Please enter Invoice Number'
        if (!invoiceHeader.date) errors.date = 'Please select Invoice Date'
        if (invoiceHeader.date && !moment(invoiceHeader.date).isValid()) errors.date = 'Please enter valid Invoice Date'
        if (!invoiceHeader.due_date) errors.due_date = 'Please select Invoice Due Date'
        if (invoiceHeader.due_date && !moment(invoiceHeader.due_date).isValid()) errors.due_date = 'Please enter valid Invoice Due Date'
        if (invoiceHeader.date && invoiceHeader.due_date) {
            if (moment(invoiceHeader.due_date).isBefore(moment(invoiceHeader.date))) {
                errors.due_date = 'Due Date cannot be before Invoice Date'
            }
        }
        if (invoiceHeader.date && filters.start_date) {
            if (moment(invoiceHeader.date).isBefore(moment(filters.start_date), 'day')) {
                errors.date = `Invoice Date cannot be before Period Start Date (${moment(filters.start_date).format('DD-MM-YYYY')})`
            }
        }
        if (invoiceHeader.due_date && filters.start_date) {
            if (moment(invoiceHeader.due_date).isBefore(moment(filters.start_date), 'day')) {
                errors.due_date = `Due Date cannot be before Period Start Date (${moment(filters.start_date).format('DD-MM-YYYY')})`
            }
        }
        if (!invoiceHeader.bank?.id) errors.bank = 'Please select Bank Details'
        if (!invoiceHeader.from?.id) errors.from = 'Please select Servicer'
        setHeaderError(errors)

        const rowErrors = {}
        selectedInvoices.forEach(inv => {
            const { mgmtFeePct, feeAmount } = getEffectiveValues(inv)
            const rowErr = {}
            if (!mgmtFeePct || mgmtFeePct <= 0) rowErr.management_fee_tax = 'Please enter Management Fee (%).'
            if (!feeAmount || feeAmount <= 0) rowErr.feeAmount = 'Please enter Fee Amount.'
            else if (feeAmount > (inv.rate_per_unit || 0)) rowErr.feeAmount = 'Fee Amount cannot exceed Amount.'
            if (Object.keys(rowErr).length) rowErrors[inv.sub_reference_id] = rowErr
        })
        setLineItemErrors(rowErrors)

        const groupNumberErrors = !createFromInv ? validateGroupInvoiceNumbers(groupInvoiceNumbers, lessorGroups) : {}
        setGroupInvoiceNumberErrors(groupNumberErrors)

        if (Object.keys(errors).length || Object.keys(rowErrors).length || Object.keys(groupNumberErrors).length) {
            return
        }

        const toOwner = createFromInv
            ? owner?.find(o => Number(o?.id) === Number(invoiceDetails?.lessor?.id))
            : null
        if (createFromInv && !toOwner) {
            enqueueSnackbar('Lessor/Holdco of this invoice not found in owner list', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
            return
        }

        const payload = createFromInv
            ? {
                invoice_number: invoiceHeader.invoice_number,
                start_date: filters.start_date || null,
                end_date: filters.end_date || null,
                date: invoiceHeader.date || null,
                due_date: invoiceHeader.due_date || null,
                currency: filters.currency?.code || null,
                bank: invoiceHeader.bank || null,
                from: invoiceHeader.from || null,
                to: toOwner,
                remarks: invoiceHeader.notes || null,
                lease_slug: invoiceDetails?.lease_slug,
                invoice_type: { label: 'Management Fee', value: 10 },
                additional_tax_type_id : filters.additional_tax_type_id || null,
                ...(createFromInv && invoiceDetails?.asset ? { asset: invoiceDetails.asset } : {}),
                invoice_items: buildInvoiceItemsPayload(selectedInvoices),
            }
            : {
                invoices: lessorGroups.map((group, index) => ({
                    invoice_number: groupInvoiceNumbers[group.lessorId] || (index === 0 ? invoiceHeader.invoice_number : incrementInvoiceNumber(invoiceHeader.invoice_number, index)),
                    start_date: filters.start_date || null,
                    end_date: filters.end_date || null,
                    date: invoiceHeader.date || null,
                    due_date: invoiceHeader.due_date || null,
                    currency: filters.currency?.code || null,
                    bank: invoiceHeader.bank || null,
                    from: invoiceHeader.from || null,
                    to: filters.lessor?.find(l => l.id === group.lessorId) || { id: group.lessorId, name: group.lessorName },
                    remarks: invoiceHeader.notes || null,
                    invoice_type: { label: 'Management Fee', value: 10 },
                    additional_tax_type_id: filters.additional_tax_type_id || null,
                    invoice_items: buildInvoiceItemsPayload(group.items),
                })),
            }

        setBackendGroupInvoiceNumberErrors({})
        setIsSubmitting(true)
        globalPostService(createFromInv ? addInvoiceApi() : addManagementFeeApi(), payload).then(response => {
            setIsSubmitting(false)
            if (checkApiStatus(response)) {
                setBackendGroupInvoiceNumberErrors({})
                if (attachments?.length) {
                    if (createFromInv) {
                        dispatch(setUploadFiles({ modal: true, slug: response?.data?.data?.slug }))
                    } else {
                        const invoiceSlugs = (response?.data?.data?.invoices || []).map(inv => inv?.slug).filter(Boolean)
                        Promise.all(invoiceSlugs.map(invoiceSlug => fileUpload({
                            attachments,
                            module_name: 'invoices',
                            slug: invoiceSlug,
                            query: { record_action_type: 'created' },
                            onError: (err) => enqueueSnackbar(err?.message || 'Error while uploading files', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } }),
                        }))).then(() => {
                            enqueueSnackbar('Attachments uploaded successfully.', { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
                            getResponseBack?.()
                        })
                    }
                }
                enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
                if (createFromInv) {
                    onToggleModal()
                    navigate(`/invoices/view/${response?.data?.data?.slug}`, { state: { from: createFromInv ? 'viewFromFleet' : from } })
                } else {
                    const generatedInvoices = response?.data?.data?.invoices || []
                    if (response?.data?.data?.generated_count === 1 && generatedInvoices[0]?.slug) {
                        onToggleModal()
                        navigate(`/invoices/view/${generatedInvoices[0].slug}`, { state: { from } })
                    } else if (generatedInvoices.length > 1) {
                        getResponseBack?.()
                        setGeneratedInvoicesModal({ modal: true, data: generatedInvoices })
                    } else {
                        onToggleModal()
                        getResponseBack?.()
                    }
                }
            } else {
                const failedGroups = !createFromInv ? response?.data?.data?.failed_groups : null
                const fieldErrors = {}
                if (Array.isArray(failedGroups)) {
                    failedGroups.forEach(failedGroup => {
                        const groupArrayIndex = failedGroup?.group_index - 1
                        const group = lessorGroups[groupArrayIndex]
                        if (!group) return
                        const reason = Array.isArray(failedGroup?.reasons) && failedGroup.reasons.length > 0
                            ? failedGroup.reasons.join(' ')
                            : (response?.data?.message || 'Invoice number is invalid.')
                        fieldErrors[group.lessorId] = reason
                    })
                }
                if (Object.keys(fieldErrors).length) {
                    setBackendGroupInvoiceNumberErrors(fieldErrors)
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
                }
            }
        })
    }

    const onViewGeneratedInvoice = (slug) => { window.open(`/invoices/view/${slug}`, '_blank') }

    const renderTableBody = () => {
        if (tableLoader) {
            return (
                <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                    </TableCell>
                </TableRow>
            )
        }
        if (!isMandatoryFilled(filters)) {
            return (
                <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Select Lessor, Period, and Currency to load eligible invoices
                    </TableCell>
                </TableRow>
            )
        }
        if (eligibleInvoices.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No eligible invoices found for the selected filters
                    </TableCell>
                </TableRow>
            )
        }
        return eligibleInvoices.map(inv => (
            <TableRow key={inv.sub_reference_id}>
                <TableCell>
                    <Checkbox
                        size="small"
                        checked={selectedInvIds.includes(inv.sub_reference_id)}
                        onChange={() => toggleRow(inv.sub_reference_id)}
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={500}>{inv.asset_details?.name || '--'}</Typography>
                    <Typography variant="caption" color="text.secondary">{inv.asset_details?.asset_type || ''}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{inv.lessor_holdco_name || '--'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{inv.invoice_item_name || '--'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{moment(inv.invoice_period_start).format(dateMonthYearDateFormat)} - {moment(inv.invoice_period_end).format(dateMonthYearDateFormat)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtCurrency(inv.rate_per_unit)}</TableCell>
                <TableCell align='center' sx={{ fontSize: 12 }}>{inv.management_fee_tax ?? '--'}%</TableCell>
                <TableCell align='center'sx={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtCurrency(calcFee(inv))}</TableCell>
            </TableRow>
        ))
    }

    return (
        <>
        <Dialog aria-labelledby="generate-mgmt-fee-dialog" className='add-lease-dialog' maxWidth="xl" fullWidth open={!generatedInvoicesModal.modal} PaperProps={{ sx: { maxWidth: (!createFromInv && activeStep === 1 && filters.additional_tax_type_id) ? 1250 : 1100 } }}>
            <DialogTitle sx={{ m: 0, p: 2, pl: 3 }} className='dialog-header' id="generate-mgmt-fee-dialog">
                Generate Management Fees
                {(!createFromInv && activeStep === 1) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                        {filters.start_date && filters.end_date && (
                            <Chip label={`${moment(filters.start_date).format(dateMonthYearDateFormat)} - ${moment(filters.end_date).format(dateMonthYearDateFormat)}`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                        {filters.currency?.code && (
                            <Chip label={filters.currency.code} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                        <Chip
                            label={`${selectedInvIds.length} Invoice${selectedInvIds.length !== 1 ? 's' : ''} selected`}
                            size="small"
                            color="success"
                            variant="filled"
                            sx={{ fontSize: 11, height: 20 }}
                        />
                        {lessorGroups.length > 0 && (
                            <Chip label={`${lessorGroups.length} Lessor${lessorGroups.length !== 1 ? 's' : ''}`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                    </Box>
                ) : createFromInv ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                        {invoiceDetails?.lessor?.name && (
                            <Chip label={`To : ${invoiceDetails.lessor.name}`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                        {invoiceCurrency && (
                            <Chip label={invoiceCurrency} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
                        Select eligible invoices to generate management fee invoice
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers>
                {!createFromInv ? <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {['Filter & Select Invoice(s)', 'Invoice Detail(s) & Line Item(s)'].map(label => (
                        <Step key={label}><StepLabel>{label}</StepLabel></Step>
                    ))}
                </Stepper>: null}

                {activeStep === 0 && (
                    <>
                        <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            Apply Filters
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    multiple
                                    disableClearable
                                    options={owner || []}
                                    getOptionLabel={opt => opt.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={filters.lessor}
                                    sx={{ '& .MuiAutocomplete-tag': { margin: '2px' } }}
                                    slotProps={{ chip: { size: "small", sx: { height: 22, fontSize: "12px" } } }}
                                    onChange={(e, val) => onLessorChange(val)}
                                    renderInput={params => (
                                        <TextField {...params} label="Lessor/Holdco" size="small" required slotProps={{ inputLabel: { shrink: true } }} />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Period Start Date"
                                        value={filters.start_date ? moment(filters.start_date) : null}
                                        onChange={val => {
                                            const isValid = moment(val)?.isValid()
                                            if (val && !isValid) {
                                                setFilterError(prev => ({ ...prev, start_date: 'Please enter valid Period Start Date' }))
                                                return
                                            }
                                            const newStart = val ? moment(val).format(backendDateFormat) : null
                                            setFilterError(prev => ({ ...prev, start_date: '' }))
                                            if (newStart && filters.end_date && moment(filters.end_date).isBefore(moment(newStart), 'day')) {
                                                setFilterError(prev => ({ ...prev, end_date: 'Period End Date cannot be before Period Start Date' }))
                                            } else {
                                                setFilterError(prev => ({ ...prev, end_date: '' }))
                                            }
                                            handleFilterChange('start_date', newStart)
                                        }}
                                        format={fieldDateFormat}
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            textField: {
                                                required: true, size: 'small', InputLabelProps: { shrink: true },
                                                error: !!filterError.start_date, helperText: filterError.start_date || '',
                                                onFocus: () => { if (filterError.start_date) setFilterError(prev => ({ ...prev, start_date: '' })) }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { handleFilterChange('start_date', null); setFilterError(prev => ({ ...prev, start_date: '' })) }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Period End Date"
                                        value={filters.end_date ? moment(filters.end_date) : null}
                                        minDate={filters.start_date ? moment(filters.start_date) : undefined}
                                        onChange={val => {
                                            const isValid = moment(val)?.isValid()
                                            if (val && !isValid) {
                                                setFilterError(prev => ({ ...prev, end_date: 'Please enter valid Period End Date' }))
                                                return
                                            }
                                            const newEnd = val ? moment(val).format(backendDateFormat) : null
                                            if (newEnd && filters.start_date && moment(newEnd).isBefore(moment(filters.start_date), 'day')) {
                                                setFilterError(prev => ({ ...prev, end_date: 'Period End Date cannot be before Period Start Date' }))
                                            } else {
                                                setFilterError(prev => ({ ...prev, end_date: '' }))
                                                handleFilterChange('end_date', newEnd)
                                            }
                                        }}
                                        format={fieldDateFormat}
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            textField: {
                                                required: true, size: 'small', InputLabelProps: { shrink: true },
                                                error: !!filterError.end_date, helperText: filterError.end_date || '',
                                                onFocus: () => { if (filterError.end_date) setFilterError(prev => ({ ...prev, end_date: '' })) }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { handleFilterChange('end_date', null); setFilterError(prev => ({ ...prev, end_date: '' })) }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    options={currencies || []}
                                    getOptionLabel={opt => opt.name || ''}
                                    value={filters.currency}
                                    onChange={(e, val) => handleFilterChange('currency', val)}
                                    renderInput={params => (
                                        <TextField {...params} label="Currency" size="small" required slotProps={{ inputLabel: { shrink: true } }} />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    options={additionalTax}
                                    loading={additionalTaxLoader}
                                    disabled={additionalTaxLoader}
                                    getOptionLabel={opt => opt.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={filters.additional_tax_type_id}
                                    onChange={(e, val) => handleFilterChange('additional_tax_type_id', val)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>Additional Tax</span>
                                                    <Tooltip
                                                        title="Additional Tax is optional. If Additional Tax is configured for the selected Asset, you must select the applicable Additional Tax here for the related data to appear in the listing."
                                                        placement="right"
                                                        arrow
                                                    >
                                                        <InfoOutlinedIcon sx={{ fontSize: '15px', color: 'text.primary', cursor: 'pointer' }} />
                                                    </Tooltip>
                                                </Box>
                                            }
                                            size="small"
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    multiple
                                    options={assetList}
                                    loading={assetLoader}
                                    disabled={assetLoader || !(filters.lessor?.length > 0)}
                                    getOptionLabel={opt => opt.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={filters.asset}
                                    sx={{ '& .MuiAutocomplete-tag': { margin: '2px' } }}
                                    slotProps={{ chip: { size: "small", sx: { height: 22, fontSize: "12px" } } }}
                                    onChange={(e, val) => handleFilterChange('asset', val)}
                                    renderInput={params => (
                                        <TextField {...params} label="Asset" size="small"
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                                input: { ...params.InputProps, endAdornment: <>{assetLoader ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }
                                            }}
                                        />
                                    )}
                                />
                            </Grid> 
                        </Grid>

                        

                        <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            Eligible Invoices
                            {eligibleInvoices.length > 0 && (
                                <Box component="span" sx={{ ml: 1, fontWeight: 400, textTransform: 'none' }}>
                                    — {eligibleInvoices.length} Invoice{eligibleInvoices.length !== 1 ? 's' : ''} found
                                </Box>
                            )}
                        </Typography>

                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table className='mui-table-format' size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 40, minWidth: 40 }}>
                                            <Checkbox
                                                size="small"
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={toggleSelectAll}
                                                disabled={eligibleInvoices.length === 0 || tableLoader}
                                            />
                                        </TableCell>
                                        <TableCell>Asset</TableCell>
                                        <TableCell>Lessor/Holdco</TableCell>
                                        <TableCell>Rental Invoice</TableCell>
                                        <TableCell>Rental Period</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell align='center' >Management Fee (%)</TableCell>
                                        <TableCell align='center'>Calculated Fee</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderTableBody()}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Grid container spacing={1}>
                            {[
                                { label: 'Selected', value: `${selectedInvIds.length} item${selectedInvIds.length !== 1 ? '(s)' : ''}` },
                                { label: 'Total Amount', value: totalFee ? fmtCurrency(totalFee) : '--' },
                                { label: 'Total Calculated Fee', value: totalCalculatedFee ? fmtCurrency(totalCalculatedFee) : '--' },
                                { label: 'Currency', value: filters.currency?.code || '--' },
                            ].map(card => (
                                <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
                                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                                            {card.label}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                                            {card.value}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {activeStep === 1 && (
                    <>
                        <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            Invoice Header
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {createFromInv && (
                                <Grid size={{ md: 4, xs: 12 }}>
                                    <TextField
                                        label="Invoice Number"
                                        size="small"
                                        required
                                        fullWidth
                                        value={invoiceHeader.invoice_number}
                                        onChange={e => { setInvoiceHeader(h => ({ ...h, invoice_number: e.target.value })); onResetErrorKey('invoice_number') }}
                                        onFocus={() => { if (headerError.invoice_number) onResetErrorKey('invoice_number') }}
                                        error={!!headerError.invoice_number}
                                        helperText={headerError.invoice_number || ''}
                                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    />
                                </Grid>
                            )}
                            <Grid size={{ md: 4, xs: 12 }}>
                                <TextField
                                    label="Invoice Status"
                                    size="small"
                                    fullWidth
                                    value="Draft"
                                    disabled
                                    slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Invoice Date"
                                        value={invoiceHeader.date ? moment(invoiceHeader.date) : null}
                                        minDate={filters.start_date ? moment(filters.start_date) : undefined}
                                        onChange={val => {
                                            const isValid = moment(val)?.isValid()
                                            if (val && !isValid) {
                                                setHeaderError(prev => ({ ...prev, date: 'Please enter valid Invoice Date' }))
                                                return
                                            }
                                            setInvoiceHeader(h => ({ ...h, date: val ? moment(val).format(backendDateFormat) : null }))
                                            if (isValid) onResetErrorKey('date')
                                        }}
                                        format={fieldDateFormat}
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            textField: {
                                                required: true, size: 'small', InputLabelProps: { shrink: true },
                                                error: !!headerError.date, helperText: headerError.date || '',
                                                onFocus: () => { if (headerError.date) onResetErrorKey('date') }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { setInvoiceHeader(h => ({ ...h, date: null })); onResetErrorKey('date') }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Invoice Due Date"
                                        value={invoiceHeader.due_date ? moment(invoiceHeader.due_date) : null}
                                        minDate={invoiceHeader.date ? moment(invoiceHeader.date) : (filters.start_date ? moment(filters.start_date) : undefined)}
                                        onChange={val => {
                                            const isValid = moment(val)?.isValid()
                                            if (val && !isValid) {
                                                setHeaderError(prev => ({ ...prev, due_date: 'Please enter valid Invoice Due Date' }))
                                                return
                                            }
                                            setInvoiceHeader(h => ({ ...h, due_date: val ? moment(val).format(backendDateFormat) : null }))
                                            if (isValid) onResetErrorKey('due_date')
                                        }}
                                        format={fieldDateFormat}
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            textField: {
                                                required: true, size: 'small', InputLabelProps: { shrink: true },
                                                error: !!headerError.due_date, helperText: headerError.due_date || '',
                                                onFocus: () => { if (headerError.due_date) onResetErrorKey('due_date') }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { setInvoiceHeader(h => ({ ...h, due_date: null })); onResetErrorKey('due_date') }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    options={owner || []}
                                    getOptionLabel={opt => opt.name || ''}
                                    value={invoiceHeader.from}
                                    onChange={(e, val) => onServicerChange(val)}
                                    renderInput={params => (
                                        <TextField {...params} label="Servicer" size="small" required onFocus={() => { if (headerError.from) onResetErrorKey('from') }} error={!!headerError.from} helperText={headerError.from || ''} slotProps={{ inputLabel: { shrink: true } }} />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    options={banks || []}
                                    loading={banksLoader}
                                    disabled={!invoiceHeader.from?.id || banksLoader}
                                    getOptionLabel={opt => opt.name ? `${opt.name}${opt.currency ? ' · ' + opt.currency : ''}${opt.iban ? ' · IBAN: ' + opt.iban : ''}` : ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={invoiceHeader.bank}
                                    onChange={(e, val) => { setInvoiceHeader(h => ({ ...h, bank: val })); onResetErrorKey('bank') }}
                                    renderInput={params => (
                                        <TextField {...params} label="Bank Details" size="small" required
                                            onFocus={() => { if (headerError.bank) onResetErrorKey('bank') }}
                                            error={!!headerError.bank || !!noBanksMsg}
                                            helperText={headerError.bank || noBanksMsg || (!invoiceHeader.from?.id ? 'Select a Servicer first' : '')}
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                                input: { ...params.InputProps, endAdornment: <>{banksLoader ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                            <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} color="text.secondary">
                                Auto-Generated {!createFromInv && invoiceHeader.from?.id ? 'Invoice Groups' : 'Line Item(s)'}
                            </Typography>
                            {!createFromInv && invoiceHeader.from?.id && lessorGroups.length > 0 && (
                                <Chip
                                    label={`${lessorGroups.length} Invoice${lessorGroups.length !== 1 ? 's' : ''} will be generated`}
                                    size="small"
                                    sx={{ backgroundColor: '#EEF1FA', color: 'text.secondary', fontWeight: 500 }}
                                />
                            )}
                        </Box>
                        {!createFromInv && invoiceHeader.from?.id ? (
                            tableLoader ? (
                                <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Paper>
                            ) : selectedInvoices.length === 0 ? (
                                <Paper variant="outlined" sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No Invoices selected</Paper>
                            ) : lessorGroups.map(group => {
                                const groupTax = group.items.reduce((sum, inv) => sum + getEffectiveValues(inv).feeAmount, 0)
                                const groupVat = group.items.reduce((sum, inv) => sum + getEffectiveValues(inv).taxAmount, 0)
                                const groupTotal = group.items.reduce((sum, inv) => sum + getEffectiveValues(inv).subTotal, 0)
                                const groupHasTax = group.items.some(inv => inv.tax_details?.length > 0)
                                return (
                                    <Paper key={group.lessorId} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 1.5, backgroundColor: '#EEF1FA' }}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    {invoiceHeader.from?.name}{group.lessorName ? ` / ${group.lessorName}` : ''}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {group.items.length} Rental Invoice{group.items.length !== 1 ? 's' : ''} · {filters.currency?.code || ''}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                label="Invoice Number"
                                                size="small"
                                                required
                                                value={groupInvoiceNumbers[group.lessorId] || ''}
                                                onChange={e => onGroupInvoiceNumberChange(group.lessorId, e.target.value)}
                                                error={!!(groupInvoiceNumberErrors[group.lessorId] || backendGroupInvoiceNumberErrors[group.lessorId])}
                                                helperText={groupInvoiceNumberErrors[group.lessorId] || backendGroupInvoiceNumberErrors[group.lessorId] || ''}
                                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                                sx={{ width: 220, backgroundColor: 'common.white' }}
                                            />
                                        </Box>
                                        <TableContainer>
                                            <Table className='mui-table-format' size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Asset Detail</TableCell>
                                                        <TableCell>Rental Invoice</TableCell>
                                                        <TableCell>Rental Period</TableCell>
                                                        <TableCell>Rental Amount</TableCell>
                                                        <TableCell align='center'>Management Fee (%)</TableCell>
                                                        <TableCell align='center'>Fee Amount</TableCell>
                                                        {groupHasTax && <TableCell align='center'>{group.items.find(inv => inv.tax_details?.length > 0)?.tax_details?.[0]?.name} (%)</TableCell>}
                                                        {groupHasTax && <TableCell align='center'>{group.items.find(inv => inv.tax_details?.length > 0)?.tax_details?.[0]?.name} Amount</TableCell>}
                                                        <TableCell>Sub Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {group.items.map(inv => {
                                                        const { mgmtFeePct, feeAmount, taxRate, taxAmount, subTotal } = getEffectiveValues(inv)
                                                        return (
                                                            <EditableLineItemRow
                                                                key={inv.sub_reference_id}
                                                                invoice={inv}
                                                                mgmtFeePct={mgmtFeePct}
                                                                feeAmount={feeAmount}
                                                                taxRate={taxRate}
                                                                taxAmount={taxAmount}
                                                                subTotal={subTotal}
                                                                showTaxColumns={groupHasTax}
                                                                showRentalColumns
                                                                errors={lineItemErrors[inv.sub_reference_id]}
                                                                onMgmtFeePctChange={rawVal => handleMgmtFeePctChange(inv, rawVal)}
                                                                onFeeAmountChange={rawVal => handleFeeAmountChange(inv, rawVal)}
                                                                onClearError={field => clearLineItemError(inv.sub_reference_id, field)}
                                                            />
                                                        )
                                                    })}
                                                    <TableRow hover={false} sx={{ backgroundColor: '#E5E7EB !important', '&:hover': { backgroundColor: '#E5E7EB !important', cursor: 'default' }, '& td': { backgroundColor: '#E5E7EB !important' } }}>
                                                        <TableCell colSpan={4} align="left" sx={{ fontWeight: 700, fontSize: 12 }}>
                                                            <strong>Invoice Total {filters.currency?.code ? `(${filters.currency.code})` : ''}</strong>
                                                        </TableCell>
                                                        <TableCell />
                                                        <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}><strong>{fmtCurrency(groupTax)}</strong></TableCell>
                                                        {groupHasTax && <TableCell />}
                                                        {groupHasTax && <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}><strong>{fmtCurrency(groupVat)}</strong></TableCell>}
                                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}><strong>{fmtCurrency(groupTotal)}</strong></TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                )
                            })
                        ) : (
                            <TableContainer component={Paper}>
                                <Table className='mui-table-format' size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Asset Detail</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell align='center'>Management Fee (%)</TableCell>
                                            <TableCell align='center'>Fee Amount</TableCell>
                                            {hasTaxDetails && <TableCell align='center'>{selectedInvoices.find(inv => inv.tax_details?.length > 0)?.tax_details?.[0]?.name} %</TableCell>}
                                            {hasTaxDetails && <TableCell align='center'>{selectedInvoices.find(inv => inv.tax_details?.length > 0)?.tax_details?.[0]?.name} Amount</TableCell>}
                                            <TableCell>Sub Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableLoader ? (
                                            <TableRow>
                                                <TableCell colSpan={hasTaxDetails ? 7 : 5} align="center" sx={{ py: 4 }}>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : selectedInvoices.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={hasTaxDetails ? 7 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                    No Invoices selected
                                                </TableCell>
                                            </TableRow>
                                        ) : selectedInvoices.map(inv => {
                                            const { mgmtFeePct, feeAmount, taxRate, taxAmount, subTotal } = getEffectiveValues(inv)
                                            return (
                                                <EditableLineItemRow
                                                    key={inv.sub_reference_id}
                                                    invoice={inv}
                                                    mgmtFeePct={mgmtFeePct}
                                                    feeAmount={feeAmount}
                                                    taxRate={taxRate}
                                                    taxAmount={taxAmount}
                                                    subTotal={subTotal}
                                                    showTaxColumns={hasTaxDetails}
                                                    errors={lineItemErrors[inv.sub_reference_id]}
                                                    onMgmtFeePctChange={rawVal => handleMgmtFeePctChange(inv, rawVal)}
                                                    onFeeAmountChange={rawVal => handleFeeAmountChange(inv, rawVal)}
                                                    onClearError={field => clearLineItemError(inv.sub_reference_id, field)}
                                                />
                                            )
                                        })}
                                        {selectedInvoices.length > 0 && (
                                            <TableRow hover={false} sx={{ backgroundColor: '#E5E7EB !important', '&:hover': { backgroundColor: '#E5E7EB !important', cursor: 'default' }, '& td': { backgroundColor: '#E5E7EB !important' } }}>
                                                <TableCell colSpan={2} align="left" sx={{ fontWeight: 700, fontSize: 12 }}>
                                                    <strong>Invoice Total {filters.currency?.code ? `(${filters.currency.code})` : ''}</strong>
                                                </TableCell>
                                                <TableCell />
                                                <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}><strong>{fmtCurrency(grandTax)}</strong></TableCell>
                                                {hasTaxDetails && <TableCell />}
                                                {hasTaxDetails && <TableCell align='center' sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}><strong>{fmtCurrency(grandVat)}</strong></TableCell>}
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}><strong>{fmtCurrency(grandTotal)}</strong></TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        <Grid container spacing={2} sx={{ mt: 3 }}>
                            <Grid size={{ xs: 12 }}> 
                                <TextField
                                    label="Remarks"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={invoiceHeader.notes}
                                    onChange={e => setInvoiceHeader(h => ({ ...h, notes: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box className="responsive-buttons">
                                    <Button disableRipple color='primary' variant='outlined' size='small' sx={{ marginBottom: '10px' }} onClick={() => handleItemClicked('Attachments Button')}>
                                        {attachments?.length ? `(${attachments.length}) Attachment(s)` : 'Attachments'}
                                    </Button>
                                    <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary'>
                                        {`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}
                                    </Button>
                                </Box>
                                <AttachementsUpload onFileChange={handleFileChange} attachments={[]} remainingStorage={storageDetails?.remaining_storage} />
                            </Grid>
                        </Grid>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between', px: 2.5, py: 1.5 }}>
                {activeStep === 1 && !createFromInv ? (
                    <Button variant='outlined' disabled={isSubmitting} className='cancel-btn' size='small' onClick={() => { setActiveStep(0); handleItemClicked('Back Button') }}>← Back</Button>
                ) : activeStep === 0 ? (
                    <Button className='cancel-btn' variant='outlined' disabled={isSubmitting} size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>
                        Cancel
                    </Button>
                ) : (
                    <Box />
                )}
                <Box display="flex" gap={1}>
                    {activeStep === 1 && (
                        <Button className='cancel-btn' variant='outlined' disabled={isSubmitting} size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>
                            Cancel
                        </Button>
                    )}
                    {activeStep === 0 && (
                        <Button
                            variant='contained'
                            size='small'
                            color='primary'
                            disabled={selectedInvIds.length === 0}
                            onClick={() => { handleProceedToStep2(); handleItemClicked('Proceed to Step 2 Button') }}
                        >
                            Proceed to Step 2 →
                        </Button>
                    )}
                    {activeStep === 1 && (
                        <Button
                            variant='contained'
                            size='small'
                            color='primary'
                            disabled={isSubmitting}
                            onClick={() => { onGenerateInvoice(); handleItemClicked('Generate Invoice Button') }}
                        >
                            {isSubmitting ? <CircularProgress color='inherit' size={20} /> : lessorGroups.length > 0 && !createFromInv  ? `Generate ${lessorGroups.length} Invoice${lessorGroups.length !== 1 ? '(s)' : ''}` : 'Generate Invoice'}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
        {generatedInvoicesModal.modal && (
            <Dialog aria-labelledby="generated-mgmt-fee-invoices-dialog" className='add-lease-dialog' maxWidth="md" fullWidth open={true}>
                <DialogTitle sx={{ m: 0, p: 2, pl: 3 }} className='dialog-header' id="generated-mgmt-fee-invoices-dialog">
                    {generatedInvoicesModal.data.length} Management Fee Invoices Generated
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                        {generatedInvoicesModal.data?.[0]?.lessor?.name && (
                            <Chip label={`Lessor/Holdco : ${generatedInvoicesModal.data[0].lessor.name}`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                        {generatedInvoicesModal.data?.[0]?.currency?.code && (
                            <Chip label={generatedInvoicesModal.data[0].currency.code} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <TableContainer component={Paper}>
                        <Table className='mui-table-format' size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice Number</TableCell>
                                    <TableCell>Lessee</TableCell>
                                    <TableCell>Invoice Period</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {generatedInvoicesModal.data.map(inv => (
                                    <TableRow key={inv.slug}>
                                        <TableCell>{inv.invoice_number || '--'}</TableCell>
                                        <TableCell>{inv?.lessee?.name || '--'}</TableCell>
                                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{inv.period ? inv.period.split(' - ').map(d => moment(d).format(dateMonthYearDateFormat)).join(' - ') : '__'} </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: inv?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{inv.amount}</NumericLabel></TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View" arrow>
                                                <IconButton size="small" onClick={() => onViewGeneratedInvoice(inv.slug)}>
                                                    <VisibilityIcon fontSize="small" color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-end', px: 2.5, py: 1.5 }}>
                    <Button
                        className='cancel-btn'
                        variant='outlined'
                        size='small'
                        onClick={() => { setGeneratedInvoicesModal({ modal: false, data: [] }); onToggleModal() }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        )}
        </>
    )
}

export default GenerateManagementFeeModal
